import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setVariableDetails } from '../../redux/reducers/variables';
import { useReactFlow } from 'reactflow';
import styled from 'styled-components';
import { RootState } from '../../redux/store';
import { handleWorkflowRun } from '../../services/method';
import { useNavigate } from 'react-router-dom';

interface VariablesPanelProps {
  data: any;
}

interface JsonTreeNode {
  key: string;
  value: any;
  path: string;
  type: string;
  isExpandable: boolean;
  children?: JsonTreeNode[];
  isFake?: boolean;
}

interface VariableInfo {
  path: string;
  type: string;
  value: any;
}

interface FieldItem {
  key: string;
  value: any;
  path: string;
  type: string;
  depth: number;
  isExpandable: boolean;
  index: string;
  isExpanded: boolean;
}

const VariableNode = ({
  node,
  depth = 0,
  expandedPaths,
  toggleExpand,
  handleDragStart,
}: {
  node: JsonTreeNode & { isFake?: boolean };
  depth?: number;
  expandedPaths: Set<string>;
  toggleExpand: (path: string) => void;
  handleDragStart: (e: React.DragEvent, path: string, value: any, type: string) => void;
}) => {
  const isExpanded = expandedPaths.has(node.path);

  const handleItemClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (node.isExpandable && !node.isFake) {
      toggleExpand(node.path);
    }
  }, [node.isExpandable, node.isFake, node.path, toggleExpand]);

  // If it's a fake item, render the fake version
  if (node.isFake && !node.path.includes("input-")) {
    return (
      <FakeJsonFieldItem $depth={depth}
        onClick={(e) => e.stopPropagation()}>
        <div className="field-header" >
          <div className="field-left">
            <span className="expand-spacer"></span>
            <span className="field-name fake-field">
              {node.key}
            </span>
          </div>
          <span className="type-badge undefined">undefined</span>
        </div>
        <div className="field-value fake-value">
          You must run workflow first
        </div>
        <div className="field-path">
          {node.path}
        </div>
      </FakeJsonFieldItem>
    );
  }

  return (
    <JsonFieldItem
      className="json-field-item"
      $depth={depth}
      onClick={node.isExpandable ? handleItemClick : (e) => e.stopPropagation()}
    >
      <div className="field-header">
        <div className="field-left">
          {node.isExpandable ? (
            <button className="expand-button" onClick={handleItemClick}>
              {isExpanded ? '▼' : '►'}
            </button>
          ) : (
            <span className="expand-spacer"></span>
          )}
          <span
            draggable={!node.isExpandable}
            onDragStart={(e) =>
              !node.isExpandable && handleDragStart(e, node.path, node.value, node.type)
            }
            className={`field-name ${node.isExpandable ? 'expandable' : 'draggable'}`}
            title={
              node.isExpandable
                ? `Click to expand: ${node.path}`
                : `Drag to insert: ${node.path}`
            }
          >
            {node.key}
          </span>
        </div>
        <span className={`type-badge ${node.type}`}>
          {node.type}
          {node.type === 'array' && Array.isArray(node.value)
            ? `[${node.value.length}]`
            : ''}
        </span>
      </div>

      {!node.isExpandable && (
        <>
          <div className="field-value">
            {typeof node.value === 'string'
              ? `"${node.value}"`
              : String(node.value)}
          </div>
          <div className="field-path">{node.path}</div>
        </>
      )}

      {node.isExpandable && isExpanded && node.children?.length ? (
        <div className="children">
          {node.children.map((child, i) => (
            <VariableNode
              key={i}
              node={child}
              depth={depth + 1}
              expandedPaths={expandedPaths}
              toggleExpand={toggleExpand}
              handleDragStart={handleDragStart}
            />
          ))}
        </div>
      ) : null}
    </JsonFieldItem>
  );
};

const FieldRenderer = ({
  field,
  toggleExpand,
  handleDragStart,
}: {
  field: FieldItem;
  toggleExpand: (path: string) => void;
  handleDragStart: (e: React.DragEvent, path: string, value: any, type: string) => void;
}) => {
  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (field.isExpandable) {
      toggleExpand(field.path);
    }
  }, [field.isExpandable, field.path, toggleExpand]);

  return (
    <JsonFieldItem
      className="json-field-item"
      $depth={field.depth}
      onClick={field.isExpandable ? handleClick : undefined}
    >
      <div className="field-header">
        <div className="field-left">
          {field.isExpandable && (
            <button
              className="expand-button"
              onClick={handleClick}
            >
              {field.isExpanded ? '▼' : '►'}
            </button>
          )}
          {!field.isExpandable && (
            <span className="expand-spacer"></span>
          )}
          <span
            draggable={!field.isExpandable}
            onDragStart={(e) => !field.isExpandable && handleDragStart(e, field.path, field.value, field.type)}
            className={`field-name ${field.isExpandable ? 'expandable' : 'draggable'}`}
            title={field.isExpandable ?
              `Click to expand: ${field.path}` :
              `Drag to insert: ${field.path}`
            }
          >
            {field.depth === 0 ? field.index : field.key}
          </span>
        </div>
        <span className={`type-badge ${field.type}`}>
          {field.type}{field.type === 'array' ? `[${field.value}]` : ''}
        </span>
      </div>

      {!field.isExpandable && (
        <>
          <div className="field-value">
            {field.type === 'string' ? `"${field.value}"` :
              field.type === 'array' ? `Array[${field.value}]` :
                String(field.value)}
          </div>
          <div className="field-path">
            {`${field.path}`}
          </div>
        </>
      )}
    </JsonFieldItem>
  );
};

export function VariablesPanel({ data }: VariablesPanelProps) {
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());
  const { getNodes, getEdges, setNodes } = useReactFlow();
  const [isRunning, setIsRunning] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const project = useSelector((state: RootState) => state.project);
  const user = useSelector((state: RootState) => state.user.data);


  const handleRunPreviousNodes = useCallback(async () => {
    if (isRunning) return; // Prevent multiple clicks

    setIsRunning(true);
    try {
      const allNodes = getNodes();
      const allEdges = getEdges();

      // Get the current node ID
      const currentNodeId = data.id;

      const relevantNodeIds = new Set<string>([currentNodeId]);
      const queue = [currentNodeId];
      const visited = new Set<string>([currentNodeId]);

      while (queue.length > 0) {
        const currentNodeId = queue.shift()!;

        // Find all input edges (nodes that feed into current node)
        const inputEdges = allEdges.filter(edge => edge.target === currentNodeId);

        for (const edge of inputEdges) {
          if (!visited.has(edge.source)) {
            visited.add(edge.source);
            relevantNodeIds.add(edge.source);
            queue.push(edge.source);
          }
        }
      }

      // Get all relevant nodes and edges
      const relevantNodes = allNodes.filter(node => relevantNodeIds.has(node.id));
      const relevantEdges = allEdges.filter(edge =>
        relevantNodeIds.has(edge.source) && relevantNodeIds.has(edge.target)
      );

      const projectId = project.id;
      const userToken = user?.token || '';
      const apiKey = project.getExportAPIKey;

      await handleWorkflowRun(
        projectId,
        userToken,
        relevantNodes,
        relevantEdges,
        false,
        false,
        apiKey,
        dispatch,
        navigate,
        setNodes
      );
    }
    catch (error) {
      console.error('Error running previous nodes:', error);
    } finally {
      setIsRunning(false); // Re-enable button and hide loader
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getNodes, getEdges, data.id, project.id, user?.token, dispatch, navigate, setNodes, isRunning]);

  // Get relevant nodes data
  const { nodes, connectedBlocksWithoutOutput } = React.useMemo(() => {
    const allNodes = JSON.parse(JSON.stringify(getNodes()));
    const allEdges = JSON.parse(JSON.stringify(getEdges()));
    // ===============================
    // Get all previous nodes (same flow)
    // ===============================
    const visited = new Set<string>();
    const stack = [data.id];

    while (stack.length) {
      const currentId = stack.pop()!;
      if (visited.has(currentId)) continue;
      visited.add(currentId);
      allEdges.filter((edge: any) => edge.target === currentId).forEach((edge: any) => {
        if (!visited.has(edge.source)) {
          stack.push(edge.source);
        }
      });
    }
    const relevantNodeIds = Array.from(visited);
    // ===============================
    // Filter nodes
    // ===============================
    let nodes = allNodes.filter(
      (node: any) =>
        // relevantNodeIds.includes(node.id) &&
        node.data.action !== "output"
    );
    // Remove value from current node only
    nodes = nodes.map((node: any) => {
      if (!relevantNodeIds.includes(node.id)) {
        const { output, ...rest } = node.data;
        return { ...node, data: rest, isHideOutputFake: true };
      }
      return node;
    });
    // ===============================
    // Blocks without output
    // ===============================
    const connectedBlocksWithoutOutput = nodes.filter(
      (node: any) =>
        node.id !== data.id &&
        !node.isHideOutputFake &&
        (!node.data.output || Object.keys(node.data.output || {}).length === 0) &&
        node.data.action !== "input"
    );
    // ===============================
    // Sort (current node first)
    // ===============================
    const connectionCountMap = new Map<string, number>();
      allEdges.forEach((edge: any) => {
        if (edge.target === data.id) connectionCountMap.set(edge.source, (connectionCountMap.get(edge.source) || 0) + 1);
    });
    nodes.sort((a: any, b: any) => {
      if (a.id === data.id) return -1;
      if (b.id === data.id) return 1;
      const aCount = connectionCountMap.get(a.id) || 0;
      const bCount = connectionCountMap.get(b.id) || 0;
      return bCount - aCount;
    });
    return { nodes, connectedBlocksWithoutOutput };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getNodes, getEdges, data.id, isRunning]);

  const toggleExpand = useCallback((path: string) => {
    setExpandedPaths(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(path)) {
        newExpanded.delete(path);
      } else {
        newExpanded.add(path);
      }
      return newExpanded;
    });
  }, []);

  const handleDragStart = useCallback((e: React.DragEvent, variablePath: string, variableValue: any, variableType: string) => {
    e.dataTransfer.setData('application/reactflow/variable', JSON.stringify({
      name: `{{${variablePath}}}`,
      value: variableValue,
      path: variablePath,
      type: variableType,
      nodeId: data.id
    }));
    e.dataTransfer.effectAllowed = 'move';
  }, [data.id]);

  // Common configuration
  const EXCLUDED_FIELDS = React.useMemo(() => new Set([
    'icon', 'action', 'category', 'outputType', 'postions',
    'variablesRef', 'executeRun', 'childOnMinimize', 'childOnSettings',
    'isMediator', 'subCategory', 'currentCategory', 'isTemporaryMemory'
  ]), []);

  // Utility functions
  const isJsonString = React.useCallback((str: any): boolean => {
    if (typeof str !== 'string') return false;
    try {
      JSON.parse(str);
      return true;
    } catch {
      return false;
    }
  }, []);

  const parseIfJson = React.useCallback((value: any): any => {
    if (typeof value === 'string' && isJsonString(value)) {
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    }
    return value;
  }, [isJsonString]);

  // Function to flatten JSON object with hierarchy
  const buildJsonTree = React.useCallback((nodesData: any[]): JsonTreeNode[] => {
    if (!nodesData || !Array.isArray(nodesData)) return [];

    const traverse = (obj: any, path: string, nodeIndex: string): JsonTreeNode => {
      const parsed = parseIfJson(obj);
      const shouldParse = parsed !== obj;
      const isObject = typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed);
      const isArray = Array.isArray(parsed);
      const isPrimitive = !isObject && !isArray;

      const prefixedPath = path
        ? `{{ $block.${nodeIndex}.${path} }}`
        : `{{ $block.${nodeIndex} }}`;

      if (isPrimitive) {
        return {
          key: path.split('.').pop() || nodeIndex,
          value: obj,
          path: prefixedPath,
          type: typeof obj,
          isExpandable: false
        };
      }

      const children: JsonTreeNode[] = [];
      const target = shouldParse ? parsed : obj;

      if (isArray) {
        target.forEach((item: any, i: number) => {
          const itemPath = path ? `${path}[${i}]` : `[${i}]`;
          children.push(traverse(item, itemPath, nodeIndex));
        });
      } else if (isObject) {
        Object.keys(target).forEach(key => {
          if (EXCLUDED_FIELDS.has(key)) return;
          const val = target[key];
          const newPath = path ? `${path}.${key}` : key;
          children.push(traverse(val, newPath, nodeIndex));
        });
      }

      return {
        key: path ? path.split('.').pop() || nodeIndex : nodeIndex,
        value: obj,
        path: prefixedPath,
        type: isArray ? 'array' : 'object',
        isExpandable: true,
        children
      };
    };

    const result: JsonTreeNode[] = [];

    nodesData.forEach(node => {
      const nodeIndex = node.data?.index || 'unknown';
      const hasOutput = node.data?.output && Object.keys(node.data.output || {}).length > 0;
      const isCurrentNode = node.id === data.id;

      const rootNode: JsonTreeNode = {
        key: nodeIndex,
        value: node.data,
        path: `{{ $block.${nodeIndex} }}`,
        type: 'object',
        isExpandable: true,
        children: []
      };

      if (node.data && typeof node.data === 'object') {
        Object.keys(node.data).forEach(key => {
          if (EXCLUDED_FIELDS.has(key)) return;
          const value = node.data[key];
          rootNode.children?.push(traverse(value, key, nodeIndex));
        });
      }

      if (!isCurrentNode && !hasOutput && node.data.action !== "input" && !nodes.find((n: any) => n.id === node.id).isHideOutputFake) {
        rootNode.children?.push({
          key: 'output',
          value: null,
          path: `{{ $block.${nodeIndex}.output }}`,
          type: 'object',
          isExpandable: false,
          isFake: true
        });
      }

      result.push(rootNode);
    });

    return result;
  }, [EXCLUDED_FIELDS, parseIfJson, data.id]);

  const getAllVariableDetails = React.useCallback((nodes: any[]): VariableInfo[] => {
    if (!nodes || !Array.isArray(nodes)) return [];

    const collectVariableDetails = (obj: any, currentPath: string, nodeIndex: string, details: VariableInfo[]) => {
      const parsed = parseIfJson(obj);
      const shouldParse = parsed !== obj;
      const target = shouldParse ? parsed : obj;

      const isObject = typeof target === 'object' && target !== null && !Array.isArray(target);
      const isArray = Array.isArray(target);

      const fullPath = currentPath ? `{{ $block.${nodeIndex}.${currentPath} }}` : `{{ $block.${nodeIndex} }}`;

      details.push({
        path: fullPath,
        type: isObject ? 'object' : isArray ? 'array' : typeof target,
        value: target
      });

      if (isObject) {
        Object.keys(target).forEach(key => {
          if (EXCLUDED_FIELDS.has(key)) return;
          const newPath = currentPath ? `${currentPath}.${key}` : key;
          collectVariableDetails(target[key], newPath, nodeIndex, details);
        });
      } else if (isArray) {
        target.forEach((item: any, index: number) => {
          const newPath = currentPath ? `${currentPath}[${index}]` : `[${index}]`;
          collectVariableDetails(item, newPath, nodeIndex, details);
        });
      }
    };

    const allDetails: VariableInfo[] = [];

    nodes.forEach(node => {
      const nodeIndex = node.data?.index || 'unknown';

      allDetails.push({
        path: `{{ $block.${nodeIndex} }}`,
        type: 'object',
        value: node.data
      });

      if (node.data && typeof node.data === 'object') {
        Object.keys(node.data).forEach(key => {
          if (EXCLUDED_FIELDS.has(key)) return;
          collectVariableDetails(node.data[key], key, nodeIndex, allDetails);
        });
      }
    });

    return allDetails;
  }, [EXCLUDED_FIELDS, parseIfJson]);

  // Process JSON tree
  const jsonTree = React.useMemo(() =>
    buildJsonTree(nodes).map(item => {
      if (item.key === data.index) {
        const { output, ...restValue } = item.value;
        const newChildren = item.children
          ? item.children.filter(child => child.key !== "output")
          : [];
        return {
          ...item,
          value: restValue,
          children: newChildren
        };
      }
      return item;
    }),
    [buildJsonTree, nodes, data.index]);

  // Global and workflow fields configuration
  const globalFields = React.useMemo((): FieldItem[] => [{
    key: "date",
    value: new Date().toUTCString(),
    path: `{{ $global.date }}`,
    type: "date",
    depth: 0,
    isExpandable: false,
    index: "Date",
    isExpanded: false,
  }], []);

  const workflowFields = React.useMemo((): FieldItem[] => {
    return [
      { key: "id", value: project.id, path: `{{ $workflow.id }}`, type: "string", depth: 0, isExpandable: false, index: "id", isExpanded: false },
      { key: "name", value: project.name, path: `{{ $workflow.name }}`, type: "string", depth: 0, isExpandable: false, index: "name", isExpanded: false },
      { key: "isLoop", value: project.isLoop, path: `{{ $workflow.isLoop }}`, type: "boolean", depth: 0, isExpandable: false, index: "isLoop", isExpanded: false },
      { key: "getExportApiUrl", value: project.getExportAPIURL, path: `{{ $workflow.getExportApiUrl }}`, type: "string", depth: 0, isExpandable: false, index: "getExportApiUrl", isExpanded: false },
      { key: "getExportApiKey", value: project.getExportAPIKey, path: `{{ $workflow.getExportApiKey }}`, type: "string", depth: 0, isExpandable: false, index: "getExportApiKey", isExpanded: false },
      { key: "getLastRunDate", value: project.getLastRunDate, path: `{{ $workflow.getLastRunDate }}`, type: "string", depth: 0, isExpandable: false, index: "getLastRunDate", isExpanded: false },
      { key: "getDateOfCreation", value: project.getDateOfCreation, path: `{{ $workflow.getDateOfCreation }}`, type: "string", depth: 0, isExpandable: false, index: "getDateOfCreation", isExpanded: false },
      { key: "getOwnerName", value: user?.fullName, path: `{{ $workflow.getOwnerName }}`, type: "string", depth: 0, isExpandable: false, index: "getOwnerName", isExpanded: false },
      { key: "getOwnerEmail", value: user?.email, path: `{{ $workflow.getOwnerEmail }}`, type: "string", depth: 0, isExpandable: false, index: "getOwnerEmail", isExpanded: false },
      { key: "getOwnerPlan", value: user?.plan?.subscription, path: `{{ $workflow.getOwnerPlan }}`, type: "string", depth: 0, isExpandable: false, index: "getOwnerPlan", isExpanded: false },
    ];
  }, []);

  // Collect all variable details for Redux
  const allVariableDetails = React.useMemo(() => getAllVariableDetails(nodes), [getAllVariableDetails, nodes]);

  const allDetails = React.useMemo(() => [
    ...allVariableDetails,
    ...globalFields.map(f => ({ path: f.path, type: f.type, value: f.value })),
    ...workflowFields.map(f => ({ path: f.path, type: f.type, value: f.value }))
  ], [allVariableDetails, globalFields, workflowFields]);

  // Dispatch to Redux
  useEffect(() => {
    dispatch(setVariableDetails(allDetails));
  }, [dispatch, allDetails, isRunning]);

  return (
    <VariablesPanelContainer>
      <PanelHeader>
        <h2>Variables</h2>
        <p>Drag and drop into any text field</p>
        <RunButton
          onClick={handleRunPreviousNodes}
          disabled={isRunning}
          className={isRunning ? 'running' : ''}>
          Run Previous Nodes
        </RunButton>
      </PanelHeader>
      {isRunning ? <Loader /> :
        <PanelContent>
          {/* Block Variables Explorer */}
          <VariableSection>
            <div className="section-title">
              <span>Block variables</span>
              <span className="count">{nodes.length} {nodes.length > 1 ? " blocks" : " block"}</span>
            </div>

            {connectedBlocksWithoutOutput.length > 0 && (
              <WarningMessage>
                <div className="warning-icon">⚠️</div>
                <div className="warning-content">
                  <div className="warning-title">Run Required</div>
                  <div className="warning-description">
                    Some connected blocks don't have output data. Run the workflow first to see available variables.
                  </div>
                </div>
              </WarningMessage>
            )}

            {jsonTree.map((node, i) => (
              <VariableNode
                key={i}
                node={node}
                depth={0}
                expandedPaths={expandedPaths}
                toggleExpand={toggleExpand}
                handleDragStart={handleDragStart}
              />
            ))}
          </VariableSection>

          {/* Global Variables Explorer */}
          <VariableSection>
            <div className="section-title">
              <span>Global variables</span>
            </div>
            <div className="variables-tree">
              {globalFields.map((field, index) => (
                <FieldRenderer
                  key={index}
                  field={field}
                  toggleExpand={toggleExpand}
                  handleDragStart={handleDragStart}
                />
              ))}
            </div>
          </VariableSection>

          {/* Workflow Variables Explorer */}
          <VariableSection>
            <div className="section-title">
              <span>Workflow variables</span>
            </div>
            <div className="variables-tree">
              {workflowFields.map((field, index) => (
                <FieldRenderer
                  key={index}
                  field={field}
                  toggleExpand={toggleExpand}
                  handleDragStart={handleDragStart}
                />
              ))}
            </div>
          </VariableSection>

          {/* Empty State for no output data */}
          {jsonTree.length === 0 && (
            <EmptyState>
              <div className="empty-icon">📄</div>
              <div className="empty-title">No output data</div>
              <div className="empty-description">
                Execute the node to see available output fields
              </div>
            </EmptyState>
          )}
        </PanelContent>
      }

    </VariablesPanelContainer>
  );
}

// Styled Components (unchanged, as they're already clean)
const VariablesPanelContainer = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  width: 400px;
  height: 100vh;
  background-color: #f8fafc;
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  z-index: 10000000;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
`;

const PanelHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid #e2e8f0;
  background-color: #fff;
  
  h2 {
    font-size: 24px;
    font-weight: 700;
    color: #1e293b;
  }
  
  p {
    margin: 0;
    font-size: 14px;
    color: #64748b;
  }
`;

const PanelContent = styled.div`
  flex: 1;
  padding: 24px;
  overflow-y: auto;
`;

const VariableSection = styled.div`
  margin-bottom: 32px;

  .section-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 16px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 2px solid #e5e7eb;

    .count {
      background-color: #3b82f6;
      color: white;
      padding: 2px 8px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 500;
    }
  }

  .variables-tree {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
`;

const JsonFieldItem = styled.div<{ $depth: number }>`
  overflow: auto;
  background-color: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  transition: all 0.2s;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  margin-bottom: 10px;

  &:hover {
    border-color: #3b82f6;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .field-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${props => props.$depth === 0 ? '8px' : '4px'};

    .field-left {
      display: flex;
      align-items: center;
    }

    .expand-button {
      background: none;
      border: none;
      cursor: pointer;
      width: 16px;
      height: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 10px;
      color: #64748b;
      border-radius: 2px;
      
      &:hover {
        background-color: #f1f5f9;
      }
    }

    .field-name {
      font-weight: 600;
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      font-size: 14px;
      
      &.expandable {
        cursor: pointer;
        color: #374151;
        padding: 4px 8px;
        border-radius: 4px;
        transition: background-color 0.2s;
        
        &:hover {
          background-color: #f1f5f9;
        }
      }
      
      &.draggable {
        cursor: grab;
        padding: 4px 8px;
        background-color: #eff6ff;
        border: 1px solid #3b82f6;
        border-radius: 4px;
        transition: all 0.2s;
        
        &:hover {
          background-color: #dbeafe;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
        }
        
        &:active {
          cursor: grabbing;
          background-color: #bfdbfe;
        }
      }
    }

    .type-badge {
      padding: 2px 6px;
      border-radius: 8px;
      font-size: 10px;
      font-weight: 500;
      text-transform: uppercase;
      
      &.string {
        background-color: #f0fdf4;
        color: #166534;
      }

      &.date {
        background-color: #faf5ff;
        color: #6a319fff;
      }
      &.number, &.boolean {
        background-color: #eff6ff;
        color: #1e40af;
      }
      
      &.array, &.object {
        background-color: #faf5ff;
        color: #7c3aed;
      }
      
      &.undefined, &.null {
        background-color: #f3f4f6;
        color: #6b7280;
      }
    }
  }

  .field-value {
    margin-top: 12px;
    font-size: 12px;
    color: #64748b;
    margin-bottom: 6px;
    word-break: break-all;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  }

  .field-path {
    font-size: 11px;
    color: #9ca3af;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    background-color: #f9fafb;
    padding: 4px 6px;
    border-radius: 4px;
    border: 1px solid #f3f4f6;
    overflow: auto;
  }
`;

const FakeJsonFieldItem = styled.div<{ $depth: number }>`
  overflow: auto;
  background-color: #fff;
  border: 1px solid #fbb6ce;
  border-radius: 8px;
  padding: 12px;
  transition: all 0.2s;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  margin-bottom: 10px;
  opacity: 0.7;


  .field-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${props => props.$depth === 0 ? '8px' : '4px'};

    .field-left {
      display: flex;
      align-items: center;
    }

    .field-name.fake-field {
      font-weight: 600;
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      font-size: 14px;
      color: #9ca3af;
      text-decoration: line-through;
    }

    .type-badge.undefined {
      padding: 2px 6px;
      border-radius: 8px;
      font-size: 10px;
      font-weight: 500;
      text-transform: uppercase;
      background-color: #f3f4f6;
      color: #6b7280;
    }
  }

  .field-value.fake-value {
    margin-top: 12px;
    font-size: 12px;
    color: #e53e3e;
    margin-bottom: 6px;
    font-weight: 500;
    font-style: italic;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  }

  .field-path {
    font-size: 11px;
    color: #9ca3af;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    background-color: #f9fafb;
    padding: 4px 6px;
    border-radius: 4px;
    border: 1px solid #f3f4f6;
    overflow: auto;
  }
`;

const WarningMessage = styled.div`
  display: flex;
  align-items: flex-start;
  background-color: #fed7d7;
  border: 1px solid #feb2b2;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;

  .warning-icon {
    font-size: 16px;
    margin-right: 8px;
    margin-top: 2px;
  }

  .warning-content {
    flex: 1;
  }

  .warning-title {
    font-weight: 600;
    font-size: 14px;
    color: #c53030;
    margin-bottom: 4px;
  }

  .warning-description {
    font-size: 12px;
    color: #742a2a;
    line-height: 1.4;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  color: #64748b;

  .empty-icon {
    font-size: 36px;
    margin-bottom: 12px;
    opacity: 0.5;
  }

  .empty-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 8px;
    color: #475569;
  }

  .empty-description {
    font-size: 14px;
    line-height: 1.5;
  }  
`;
const RunButton = styled.button`
  background-color: #3b82f6;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background-color: #2563eb;
  }
  
  &:active:not(:disabled) {
    background-color: #1d4ed8;
  }
  
  &:disabled {
    background-color: #9ca3af;
    cursor: not-allowed;
    opacity: 0.7;
  }
  
  
`;
const Loader = styled.span`
    width: 68px;
    height: 68px;
    border: 5px solid #9ca3af;
    border-bottom-color: transparent;
    border-radius: 50%;
    display: inline-block;
    box-sizing: border-box;
    animation: rotation 1s linear infinite;
    margin: 0 auto;
    margin-top: 40%;
    

    @keyframes rotation {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
  }
`;